import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

interface AudioPlayerProps {
  src: string;
  title?: string;
  autoPlay?: boolean;
  onEnded?: () => void;
  className?: string;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, autoPlay = false, className = '' }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      setProgress(audio.currentTime);
      setDuration(audio.duration || 0);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', updateProgress);
    
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('loadedmetadata', updateProgress);
    };
  }, []);

  const togglePlay = () => {
    if (audioRef.current?.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setProgress(time);
    }
  };

  return (
    <div className={`flex items-center gap-3 bg-card text-card-foreground p-2 rounded-lg border border-border w-full max-w-sm ${className}`}>
      <button onClick={togglePlay} className="text-primary hover:opacity-80 transition">
        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
      </button>

      <input
        type="range"
        min={0}
        max={duration || 100}
        value={progress}
        onChange={handleSeek}
        className="flex-1 h-1 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
      />
      
      <span className="text-[10px] font-mono text-muted-foreground min-w-[30px]">
        {Math.floor(progress / 60)}:{Math.floor(progress % 60).toString().padStart(2, '0')}
      </span>

      <audio ref={audioRef} src={src} autoPlay={autoPlay} />
    </div>
  );
};